import {
  EntityActionOptions,
  EntityCollectionServiceBase,
  EntityCollectionServiceElementsFactory,
  EntityOp,
  EntitySelectors$,
  QueryParams,
} from '@ngrx/data';
import { getQueryParams } from '@shared/api';
import { isArrayOfStrings } from '@shared/utils';
import { isArray, isNumber, isString } from 'lodash-es';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { EntityInterface } from './entity.interface';

export class CustomEntityCollectionBaseService<
  T extends EntityInterface,
  S$ extends EntitySelectors$<T> = EntitySelectors$<T>,
> extends EntityCollectionServiceBase<T, S$> {
  constructor(
    entityName: string,
    serviceElementsFactory: EntityCollectionServiceElementsFactory,
  ) {
    super(entityName, serviceElementsFactory);
  }

  refresh(params?: Record<string, any>): void {
    this.createAndDispatch(<EntityOp>'REFRESH', params);
  }

  finish(): void {
    this.createAndDispatch(<EntityOp>'FINISH');
  }

  start(params?: Record<string, any>): void {
    this.createAndDispatch(<EntityOp>'START', params);
  }

  getByKeys(
    items: T[] | string[],
    options?: EntityActionOptions,
  ): Observable<T[]> {
    if (isArrayOfStrings(items)) {
      return this.getWithQuery({ guid: items as string[] }, options);
    } else {
      return this.getWithQuery(
        { guid: (items as T[]).map((item) => item.guid) },
        options,
      );
    }
  }

  override update(
    entity: Partial<T>,
    options?: EntityActionOptions,
    ids?: string[],
  ): Observable<T> {
    if (ids) {
      const items = ids.map((id) => ({ ...entity, guid: id }));

      if (options?.isOptimistic) {
        this.updateManyInCache(items);
      }

      return super.update({ ...entity, guid: ids.join(',') }, options).pipe(
        tap(() => {
          if (!options?.isOptimistic) {
            this.updateManyInCache(items);
          }
        }),
      );
    } else {
      return super.update(entity, options);
    }
  }

  override delete(
    entity: T | T[] | string | number,
    options?: EntityActionOptions,
  ): Observable<number | string> {
    if (isString(entity) || isNumber(entity)) {
      return super.delete(entity, options);
    } else {
      if (isArray(entity)) {
        if (options?.isOptimistic) {
          this.removeManyFromCache(entity);
        }

        return super
          .delete(entity.map((item) => item.guid).join(','), options)
          .pipe(
            tap(() => {
              if (!options?.isOptimistic) {
                this.removeManyFromCache(entity);
              }
            }),
          );
      } else {
        return super.delete(entity.guid, options);
      }
    }
  }

  override getWithQuery(
    queryParams: QueryParams,
    options?: EntityActionOptions,
  ): Observable<T[]> {
    return super.getWithQuery(getQueryParams(queryParams), options);
  }
}
